import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Coffee, Utensils, Wine, GlassWater, Landmark, Info, AlertTriangle, Lightbulb, MapPin, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import GuideEditorDialog from "./GuideEditorDialog";

const CATEGORY_META = {
  city_guide:     { label: "Guías de ciudad",       icon: BookOpen,       color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  restaurants:    { label: "Restaurantes",          icon: Utensils,       color: "bg-orange-100 text-orange-800 border-orange-300" },
  bars_nightlife: { label: "Bares y Nightlife",     icon: Wine,           color: "bg-purple-100 text-purple-800 border-purple-300" },
  cafes_bakeries: { label: "Cafés y panaderías",    icon: Coffee,         color: "bg-amber-100 text-amber-800 border-amber-300" },
  gastronomy:     { label: "Gastronomía",           icon: GlassWater,     color: "bg-rose-100 text-rose-800 border-rose-300" },
  cultural:       { label: "Cultura y tradiciones", icon: Landmark,       color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  tips:           { label: "Consejos",              icon: Lightbulb,      color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  general_info:   { label: "Información general",   icon: Info,           color: "bg-sky-100 text-sky-800 border-sky-300" },
  disclaimer:     { label: "Disclaimers / Legales", icon: AlertTriangle,  color: "bg-slate-100 text-slate-700 border-slate-300" },
};

const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

export default function GuidesTipsTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("__all__");
  const [location, setLocation] = useState("__all__");
  const [preview, setPreview] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [onlyEnriched, setOnlyEnriched] = useState(false);

  // Convertir Guía editorial → TD (service). Solo pide confirmación.
  const convertMut = useMutation({
    mutationFn: (pid) => api.post(`/plantillas/${pid}/convert`, { to: "service" }),
    onSuccess: () => {
      toast.success("Movida a Travel Document. Recuerda clasificar el tipo de servicio con AI o manualmente.");
      qc.invalidateQueries({ queryKey: ["editorial-summary"] });
      qc.invalidateQueries({ queryKey: ["editorial-list"] });
      qc.invalidateQueries({ queryKey: ["plantillas"] });
      qc.invalidateQueries({ queryKey: ["plantillas-meta"] });
      setConvertTarget(null);
      setPreview(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const summaryQ = useQuery({
    queryKey: ["editorial-summary"],
    queryFn: async () => (await api.get("/plantillas/editorial/summary")).data,
    staleTime: 60_000,
  });

  const listQ = useQuery({
    queryKey: ["editorial-list", q, category, location],
    queryFn: async () => {
      const params = {
        content_type: "editorial",
        limit: 700,
      };
      if (q) params.q = q;
      if (category !== "__all__") params.editorial_category = category;
      if (location !== "__all__") params.location = location;
      return (await api.get("/plantillas", { params })).data;
    },
  });

  const grouped = useMemo(() => {
    const g = {};
    (listQ.data?.items || []).forEach((it) => {
      if (onlyEnriched && !(it.suggestions?.length > 0)) return;
      const cat = it.editorial_category || "other_editorial";
      if (!g[cat]) g[cat] = [];
      g[cat].push(it);
    });
    return g;
  }, [listQ.data, onlyEnriched]);

  const enrichedCount = useMemo(
    () => (listQ.data?.items || []).filter((it) => it.suggestions?.length > 0).length,
    [listQ.data],
  );

  const categories = Object.entries(summaryQ.data?.by_category || {}).sort((a, b) => b[1] - a[1]);
  const locations = summaryQ.data?.by_location || [];

  return (
    <div className="space-y-4" data-testid="tab-guides-content">
      <div className="rounded-lg border bg-indigo-50 p-3 text-sm text-indigo-900">
        <strong>Guías y consejos</strong> — contenidos editoriales sin reserva concreta.
        Aparecen solo aquí y en el editor de Travel Documents (como <em>extras por ciudad</em>).
        NO aparecen en el buscador del Cotizador.
      </div>

      {/* Resumen por categoría */}
      {summaryQ.data && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9" data-testid="editorial-summary">
          <button
            onClick={() => setCategory("__all__")}
            className={`rounded-lg border p-2 text-left transition-colors hover:bg-accent ${category === "__all__" ? "border-indigo-500 bg-indigo-50" : "border-slate-200"}`}
          >
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Todas</div>
            <div className="text-lg font-semibold">{summaryQ.data.total}</div>
          </button>
          {categories.map(([cat, count]) => {
            const meta = CATEGORY_META[cat] || { label: cat, icon: Info, color: "bg-slate-100" };
            const Icon = meta.icon;
            const active = category === cat;
            return (
              <button
                key={cat}
                onClick={() => setCategory(active ? "__all__" : cat)}
                className={`flex items-start gap-2 rounded-lg border p-2 text-left transition-colors hover:bg-accent ${active ? "border-indigo-500 bg-indigo-50" : "border-slate-200"}`}
                data-testid={`editorial-cat-${cat}`}
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
                <div className="min-w-0">
                  <div className="truncate text-[10px] uppercase tracking-wide text-slate-500">{meta.label}</div>
                  <div className="text-lg font-semibold">{count}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre…"
            className="pl-8"
            data-testid="editorial-search"
          />
        </div>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className="w-[220px]" data-testid="editorial-loc-select">
            <SelectValue placeholder="Ciudad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas las ciudades</SelectItem>
            {locations.map((l) => (
              <SelectItem key={l.location} value={l.location}>
                {l.location} ({l.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(q || category !== "__all__" || location !== "__all__") && (
          <Button variant="outline" size="sm" onClick={() => { setQ(""); setCategory("__all__"); setLocation("__all__"); }}>
            Limpiar filtros
          </Button>
        )}
        <Button
          variant={onlyEnriched ? "default" : "outline"}
          size="sm"
          onClick={() => setOnlyEnriched((v) => !v)}
          className={onlyEnriched ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          data-testid="filter-only-enriched"
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          Solo con sugerencias Travefy ({enrichedCount})
        </Button>
      </div>

      {/* Listado agrupado por categoría */}
      {listQ.isLoading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : (listQ.data?.total || 0) === 0 ? (
        <div className="rounded-lg border bg-white p-8 text-center text-sm text-muted-foreground">
          Sin resultados con esos filtros.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, items]) => {
            const meta = CATEGORY_META[cat] || { label: cat, icon: Info, color: "bg-slate-100 text-slate-700 border-slate-300" };
            const Icon = meta.icon;
            return (
              <div key={cat}>
                <div className="mb-2 flex items-center gap-2">
                  <Icon className="h-4 w-4 text-slate-600" />
                  <h3 className="text-sm font-semibold text-slate-800">{meta.label}</h3>
                  <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {items.map((it) => (
                    <Card
                      key={it.id}
                      className="cursor-pointer hover:border-indigo-300 hover:shadow-sm"
                      onClick={() => setPreview(it)}
                      data-testid={`editorial-card-${it.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="mb-1 flex items-start justify-between gap-2">
                          <span className="line-clamp-2 text-sm font-medium">{it.name}</span>
                          <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${meta.color}`}>
                            {it.type}
                          </span>
                        </div>
                        {(it.suggestions?.length || 0) > 0 && (
                          <div className="mb-1 flex items-center gap-1">
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                              <Sparkles className="h-3 w-3" />
                              {it.suggestions.length} sugerencia{it.suggestions.length !== 1 ? "s" : ""} Travefy
                            </span>
                          </div>
                        )}
                        {it.location && (
                          <div className="mb-1 flex items-center gap-1 text-[11px] text-slate-500">
                            <MapPin className="h-3 w-3" />
                            <span>{it.location}</span>
                          </div>
                        )}
                        <p className="line-clamp-2 text-[11px] text-slate-600">{stripHtml(it.description).slice(0, 160)}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview + Editor modal */}
      <GuideEditorDialog
        guide={preview}
        onClose={() => setPreview(null)}
        onConvertToTd={(g) => setConvertTarget(g)}
        categoryMeta={CATEGORY_META}
      />

      {/* Confirmación conversión Guía → TD */}
      <AlertDialog open={!!convertTarget} onOpenChange={(o) => !o && setConvertTarget(null)}>
        <AlertDialogContent data-testid="editorial-convert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Mover &quot;{convertTarget?.name}&quot; a Travel Documents?</AlertDialogTitle>
            <AlertDialogDescription>
              Dejará de aparecer aquí y se moverá al módulo <b>Travel Document (TD)</b>.
              Aparecerá como <b>Sin clasificar</b> hasta que le asignes un <em>tipo de servicio</em>
              (manualmente o con el botón <em>Reclasificar con AI</em>).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => convertMut.mutate(convertTarget.id)}
              data-testid="editorial-convert-confirm"
            >
              Mover a TD
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
